import { AlertTriangle, Loader2, Pencil, Sparkles } from "lucide-react";
import type { FC } from "react";

import type {
  OutgoingIntakeFormNode,
  OutgoingIntakeLintIssue
} from "@/api/types/outgoingIntake";
import { Button } from "@/components/ui/button";
import { ListCard } from "@/components/ui/list-card";

import { clip, issuePlace, lintHeadline } from "./intakeLint";

type Props = {
  status: "checking" | "done" | "failed";
  issues: OutgoingIntakeLintIssue[];
  schema: OutgoingIntakeFormNode[];
  /** The client's first name, or "the client". */
  who: string;
  /** Use the suggested rewrite. */
  onRewrite: (issue: OutgoingIntakeLintIssue) => void;
  /** Go to the text to edit it by hand. */
  onEdit: (issue: OutgoingIntakeLintIssue) => void;
  onDismiss: (issue: OutgoingIntakeLintIssue) => void;
  /** Whether this sheet can change that text (the note always; questions
   * only from the editor). */
  canChange: (issue: OutgoingIntakeLintIssue) => boolean;
};

/**
 * "1 thing to fix before sending. The helper text on question 2 reads like
 * an internal note, and Amara would see it word for word."
 */
export const LintBanner: FC<Props> = ({
  status,
  issues,
  schema,
  who,
  onRewrite,
  onEdit,
  onDismiss,
  canChange
}) => {
  if (status === "checking") {
    return (
      <div
        className="flex items-center gap-2 text-sm text-fg-3"
        data-testid="send-lint-checking"
      >
        <Loader2 className="size-3.5 animate-spin" />
        Checking the wording {who} will see…
      </div>
    );
  }
  if (!issues.length) return null;
  const Who = who.charAt(0).toUpperCase() + who.slice(1);
  return (
    <ListCard tone="warn" role="alert" data-testid="send-lint">
      <div className="flex items-center gap-1.5 px-3 py-2 text-base font-medium text-warn">
        <AlertTriangle className="size-3.5 shrink-0" />
        {lintHeadline(issues.length)}
      </div>
      {issues.map((issue) => {
        // An empty rewrite drops the text: fine for helper text or the note,
        // never for the question itself.
        const changeable = canChange(issue);
        const canRewrite =
          changeable &&
          typeof issue.suggestion === "string" &&
          (issue.suggestion.trim() !== "" ||
            issue.field === "help" ||
            issue.field === "note");
        return (
          <div
            key={issue.key}
            className="flex flex-col gap-2 px-3 py-2.5 text-base text-fg-2"
          >
            <p>
              {issuePlace(issue, schema)}: “{clip(issue.text)}”. {issue.reason}{" "}
              {Who} would see it word for word.
            </p>
            {canRewrite && issue.suggestion ? (
              <p className="rounded-md bg-fill-1 px-2 py-1 text-fg">
                <span className="text-fg-3">Suggested: </span>“
                {clip(issue.suggestion, 220)}”
              </p>
            ) : null}
            <div className="flex flex-wrap gap-1.5">
              {canRewrite ? (
                <Button
                  variant="primary"
                  size="xs"
                  onClick={() => onRewrite(issue)}
                >
                  <Sparkles />
                  {issue.suggestion ? `Rewrite for ${Who}` : "Remove It"}
                </Button>
              ) : null}
              {changeable && (issue.node_id || issue.field === "note") ? (
                <Button
                  size="xs"
                  variant="secondary"
                  onClick={() => onEdit(issue)}
                >
                  <Pencil />
                  Edit It Myself
                </Button>
              ) : null}
              <Button
                size="xs"
                variant="ghost"
                onClick={() => onDismiss(issue)}
              >
                It&apos;s Fine
              </Button>
            </div>
          </div>
        );
      })}
    </ListCard>
  );
};
